// Indonesian UI labels for the contract identifiers (docs/contract.md).
import type { DocStatus, DocumentEvent, Role, Tahap, TransitionAction } from "../api";

export const ROLE_LABEL: Record<Role, string> = {
  admin: "Administrator",
  pa_kpa: "PA/KPA",
  ppk: "PPK",
  pokja: "Pokja Pemilihan",
  pejabat_pengadaan: "Pejabat Pengadaan",
  tim_teknis: "Tim Teknis",
  tim_swakelola: "Tim Swakelola",
  penyedia: "Penyedia",
  konsultan: "Konsultan",
  pending: "Belum memilih peran",
};

/** The Non-Penyedia roles: picked at onboarding, and switched from the sidebar Mode menu (Google accounts). */
export const NON_PENYEDIA_ROLES = ["pa_kpa", "ppk", "pokja", "pejabat_pengadaan", "tim_teknis", "tim_swakelola"] as const satisfies readonly Role[];

export function isNonPenyedia(role: Role): role is (typeof NON_PENYEDIA_ROLES)[number] {
  return (NON_PENYEDIA_ROLES as readonly string[]).includes(role);
}

/** Roles an administrator may assign. "pending" is set by the system at Google sign-up, never picked. */
export const ROLE_OPTIONS: Role[] = ["admin", "pa_kpa", "ppk", "pokja", "pejabat_pengadaan", "tim_teknis", "tim_swakelola", "penyedia", "konsultan"];

export const STATUS_LABEL: Record<DocStatus, string> = {
  draft: "Draft",
  review: "Review",
  revisi: "Revisi",
  final: "Final",
};

export const STATUS_ORDER: DocStatus[] = ["draft", "review", "revisi", "final"];

/** One line per status, used on the Beranda counts. */
export const STATUS_HINT: Record<DocStatus, string> = {
  draft: "Belum diajukan",
  review: "Menunggu keputusan",
  revisi: "Perlu diperbaiki",
  final: "Sudah ditetapkan",
};

export const TAHAP_LABEL: Record<Tahap, string> = {
  perencanaan: "Perencanaan Pengadaan",
  persiapan_pengadaan: "Persiapan Pengadaan",
  persiapan_pemilihan: "Persiapan Pemilihan",
  pelaksanaan_pemilihan: "Pelaksanaan Pemilihan",
};

export const TAHAP_ORDER: Tahap[] = ["perencanaan", "persiapan_pengadaan", "persiapan_pemilihan", "pelaksanaan_pemilihan"];

/** Tahap each role may generate (docs/contract.md section 1). */
export const ROLE_TAHAP: Record<Role, Tahap[]> = {
  admin: [],
  pa_kpa: ["perencanaan", "persiapan_pengadaan"],
  ppk: ["perencanaan", "persiapan_pengadaan", "persiapan_pemilihan"],
  pokja: ["perencanaan", "persiapan_pengadaan", "persiapan_pemilihan"],
  pejabat_pengadaan: ["perencanaan", "persiapan_pengadaan", "persiapan_pemilihan"],
  tim_teknis: ["perencanaan", "persiapan_pengadaan", "persiapan_pemilihan"],
  tim_swakelola: ["perencanaan", "persiapan_pengadaan", "persiapan_pemilihan"],
  penyedia: ["pelaksanaan_pemilihan"],
  konsultan: [],
  pending: [],
};

export function canGenerate(role: Role): boolean {
  return ROLE_TAHAP[role].length > 0;
}

export const ACTION_LABEL: Record<TransitionAction, string> = {
  submit: "Ajukan Review",
  approve: "Setujui",
  request_revision: "Minta Revisi",
  finalize: "Finalisasi",
  reopen: "Buka Kembali",
};

/** Display order of workflow buttons; the first allowed forward action is primary. */
export const ACTION_ORDER: TransitionAction[] = ["approve", "submit", "finalize", "request_revision", "reopen"];
export const FORWARD_ACTIONS: TransitionAction[] = ["approve", "submit", "finalize"];

export const ACTION_CONFIRM: Record<TransitionAction, { title: string; body: string; confirm: string; noteRequired: boolean }> = {
  submit: {
    title: "Ajukan dokumen untuk review?",
    body: "Dokumen dikirim ke PA/KPA. Anda tidak dapat mengubahnya selama direview.",
    confirm: "Ajukan Review",
    noteRequired: false,
  },
  approve: {
    title: "Setujui dokumen ini?",
    body: "Status menjadi Final. Pastikan isi sudah Anda periksa, karena draf ini disusun Pluto.",
    confirm: "Setujui",
    noteRequired: false,
  },
  request_revision: {
    title: "Minta revisi dari PPK?",
    body: "Tulis apa yang perlu diperbaiki. Catatan ini tampil di dokumen untuk pembuatnya.",
    confirm: "Kirim permintaan revisi",
    noteRequired: true,
  },
  finalize: {
    title: "Finalisasi dokumen ini?",
    body: "Status menjadi Final. Pastikan isi sudah Anda periksa, karena draf ini disusun Pluto.",
    confirm: "Finalisasi",
    noteRequired: false,
  },
  reopen: {
    title: "Buka kembali dokumen ini?",
    body: "Status kembali menjadi Revisi sehingga isinya dapat diubah lagi.",
    confirm: "Buka Kembali",
    noteRequired: false,
  },
};

/**
 * A Penyedia finalizes its own drafts with no disposition chain: the button
 * and its confirmation speak of marking the document final, not of
 * "Finalisasi" by an official.
 */
const PENYEDIA_FINALIZE = {
  label: "Tandai Final",
  confirm: {
    title: "Tandai dokumen ini final?",
    body: "Status menjadi Final sebagai versi pegangan Anda. Pastikan isinya sudah sesuai dokumen pemilihan, karena draf ini disusun Pluto.",
    confirm: "Tandai Final",
    noteRequired: false,
  },
};

export function actionLabel(action: TransitionAction, penyedia = false): string {
  return penyedia && action === "finalize" ? PENYEDIA_FINALIZE.label : ACTION_LABEL[action];
}

export function actionConfirm(action: TransitionAction, penyedia = false): (typeof ACTION_CONFIRM)[TransitionAction] {
  return penyedia && action === "finalize" ? PENYEDIA_FINALIZE.confirm : ACTION_CONFIRM[action];
}

export const EVENT_LABEL: Record<DocumentEvent["action"], string> = {
  generate: "Draf disusun Pluto",
  edit: "Isi diubah",
  regenerate: "Draf disusun ulang Pluto",
  submit: "Diajukan untuk review",
  approve: "Disetujui",
  request_revision: "Diminta revisi",
  finalize: "Difinalisasi",
  reopen: "Dibuka kembali",
};

export const KNOWLEDGE_CATEGORY_LABEL: Record<string, string> = {
  regulasi: "Regulasi",
  template: "Templat",
  panduan: "Panduan",
  contoh: "Contoh",
};

export function isTransitionAction(value: string): value is TransitionAction {
  return (ACTION_ORDER as string[]).includes(value);
}
